import { z } from "zod";
import {
  HotelServiceAutoCreateTrigger,
  HotelServiceProviderType,
  HotelServiceStatus,
  HotelServiceType,
  ServiceDisplaySettings,
  WeekDay,
} from "@/types/hotelService";

const guestStageVisibilitySchema = z.object({
  preCheckIn: z.boolean(),
  inHouse: z.boolean(),
  preCheckOut: z.boolean(),
});

export const defaultServiceDisplaySettings = {
  checkInCheckOut: { preCheckIn: true, inHouse: true, preCheckOut: true },
  carousel: { preCheckIn: true, inHouse: true, preCheckOut: true },
  specialServices: { preCheckIn: true, inHouse: true, preCheckOut: true },
  ctaList: { preCheckIn: true, inHouse: true, preCheckOut: true },
  chatDisplay: { preCheckIn: true, inHouse: true, preCheckOut: true },
} as const;

export type ServiceDisplaySettingsForm = {
  checkInCheckOut: z.infer<typeof guestStageVisibilitySchema>;
  carousel: z.infer<typeof guestStageVisibilitySchema>;
  specialServices: z.infer<typeof guestStageVisibilitySchema>;
  ctaList: z.infer<typeof guestStageVisibilitySchema>;
  chatDisplay: z.infer<typeof guestStageVisibilitySchema>;
};

export const mergeServiceDisplaySettings = (
  partial?: Partial<{
    checkInCheckOut?: Partial<ServiceDisplaySettingsForm["checkInCheckOut"]>;
    carousel?: Partial<ServiceDisplaySettingsForm["carousel"]>;
    specialServices?: Partial<ServiceDisplaySettingsForm["specialServices"]>;
    ctaList?: Partial<ServiceDisplaySettingsForm["ctaList"]>;
    chatDisplay?: Partial<ServiceDisplaySettingsForm["chatDisplay"]>;
  }>,
): ServiceDisplaySettingsForm => ({
  checkInCheckOut: {
    ...defaultServiceDisplaySettings.checkInCheckOut,
    ...partial?.checkInCheckOut,
  },
  carousel: {
    ...defaultServiceDisplaySettings.carousel,
    ...partial?.carousel,
  },
  specialServices: {
    ...defaultServiceDisplaySettings.specialServices,
    ...partial?.specialServices,
  },
  ctaList: {
    ...defaultServiceDisplaySettings.ctaList,
    ...partial?.ctaList,
  },
  chatDisplay: {
    ...defaultServiceDisplaySettings.chatDisplay,
    ...partial?.chatDisplay,
  },
});

export const checkInOutAvailabilityFromDisplayRow = (
  row: ServiceDisplaySettingsForm["checkInCheckOut"],
) => ({
  checkinAvailable: row.preCheckIn || row.inHouse,
  checkoutAvailable: row.preCheckOut || row.inHouse,
});

export const mapServiceDisplaySettingsToRequest = (
  settings: ServiceDisplaySettingsForm,
): ServiceDisplaySettings => ({
  beforeCheckin: {
    checkinCheckout: settings.checkInCheckOut.preCheckIn,
    dashboardCarousel: settings.carousel.preCheckIn,
    dashboardCtaList: settings.ctaList.preCheckIn,
    dashboardRecommendedServices: settings.specialServices.preCheckIn,
    chatDisplay: settings.chatDisplay.preCheckIn,
  },
  inHouse: {
    checkinCheckout: settings.checkInCheckOut.inHouse,
    dashboardCarousel: settings.carousel.inHouse,
    dashboardCtaList: settings.ctaList.inHouse,
    dashboardRecommendedServices: settings.specialServices.inHouse,
    chatDisplay: settings.chatDisplay.inHouse,
  },
  beforeCheckout: {
    checkinCheckout: settings.checkInCheckOut.preCheckOut,
    dashboardCarousel: settings.carousel.preCheckOut,
    dashboardCtaList: settings.ctaList.preCheckOut,
    dashboardRecommendedServices: settings.specialServices.preCheckOut,
    chatDisplay: settings.chatDisplay.preCheckOut,
  },
});

export const mapServiceDisplaySettingsToForm = (
  settings: ServiceDisplaySettings,
): ServiceDisplaySettingsForm => ({
  checkInCheckOut: {
    preCheckIn: settings.beforeCheckin.checkinCheckout,
    inHouse: settings.inHouse.checkinCheckout,
    preCheckOut: settings.beforeCheckout.checkinCheckout,
  },
  carousel: {
    preCheckIn: settings.beforeCheckin.dashboardCarousel,
    inHouse: settings.inHouse.dashboardCarousel,
    preCheckOut: settings.beforeCheckout.dashboardCarousel,
  },
  specialServices: {
    preCheckIn: settings.beforeCheckin.dashboardRecommendedServices,
    inHouse: settings.inHouse.dashboardRecommendedServices,
    preCheckOut: settings.beforeCheckout.dashboardRecommendedServices,
  },
  ctaList: {
    preCheckIn: settings.beforeCheckin.dashboardCtaList,
    inHouse: settings.inHouse.dashboardCtaList,
    preCheckOut: settings.beforeCheckout.dashboardCtaList,
  },
  chatDisplay: {
    preCheckIn: settings.beforeCheckin.chatDisplay,
    inHouse: settings.inHouse.chatDisplay,
    preCheckOut: settings.beforeCheckout.chatDisplay,
  },
});

export const createHotelServiceSchema = z
  .object({
    name: z.string().min(1, "Hotel service name must be a string"),

    photoId: z.string().optional(),

    amenityId: z.string().optional(),

    description: z
      .string()
      .min(1, "Hotel service description must be a string"),
    price: z
      .string()
      .optional()
      .refine((val) => !val || !isNaN(Number(val)), {
        message: "Price must be a valid number",
      }),

    discount: z
      .string()
      .optional()
      .refine((val) => !val || !isNaN(Number(val)), {
        message: "Discount must be a valid number",
      })
      .refine((val) => !val || Number(val) >= 0, {
        message: "Discount cannot be less than 0%",
      })
      .refine((val) => !val || Number(val) <= 100, {
        message: "Discount cannot be more than 100%",
      }),

    providerType: z.enum(HotelServiceProviderType),

    categoryId: z.string(),

    status: z.enum(HotelServiceStatus),

    type: z.enum(HotelServiceType).optional(),

    isRecommended: z.boolean(),

    autoCreateTriggers: z.array(z.enum(HotelServiceAutoCreateTrigger)),

    availableFromTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
      message: '`availableFrom` must be in HH:mm format (e.g., "09:00")',
    }),

    availableToTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
      message: '`availableTo` must be in HH:mm format (e.g., "17:30")',
    }),

    availableDays: z
      .array(z.enum(WeekDay))
      .min(1, "At least one available day is required")
      .refine((days) => new Set(days).size === days.length, {
        message: "Available days must be unique",
      }),
    contractor: z
      .object({
        name: z.string().min(1, "Contractor name is required"),
        surname: z.string().min(1, "Contractor surname is required"),
        phone: z.string().min(1, "Contractor phone is required"),
        email: z.string().email("Invalid contractor email"),
        fee: z.string().refine((val) => !val || !isNaN(Number(val)), {
          message: "Fee must be a valid number",
        }),
      })
      .optional(),

    staffId: z.uuid().optional(),

    teamId: z.uuid().optional(),

    serviceDisplaySettings: z.object({
      checkInCheckOut: guestStageVisibilitySchema,
      carousel: guestStageVisibilitySchema,
      specialServices: guestStageVisibilitySchema,
      ctaList: guestStageVisibilitySchema,
      chatDisplay: guestStageVisibilitySchema,
    }),
  })
  .refine(
    async ({ availableFromTime, availableToTime }) => {
      const [fromH, fromM] = availableFromTime.split(":").map(Number);
      const [toH, toM] = availableToTime.split(":").map(Number);

      const fromTotal = fromH * 60 + fromM;
      const toTotal = toH * 60 + toM;

      return fromTotal < toTotal; // Must be strictly earlier
    },
    {
      message: "`availableFromTime` must be earlier than `availableToTime`",
      path: ["availableToTime"], // Error will be attached to this field
    },
  );

export type CreateHotelServiceFormData = z.infer<
  typeof createHotelServiceSchema
>;
